const projector = require('ecef-projector');

function degressToRadians(degree) {
    return degree * (Math.PI / 180);
}

/**
 * Converts from local building aligned coordinates to
 * ENU coordiantes by rotating the vector in the buildingToEnuRotation.
 * See https://matthew-brett.github.io/teaching/rotation_2d.html and also
 * in Matlab file lclToGeo.m
 * @param {number} buildingToEnuRotation 
 * @param {x: number, y: number} localBuildingCoordinates 
 */
function buildingToENU(buildingToEnuRotation, { x, y }) {
    const radRotation = degressToRadians(buildingToEnuRotation);
    return {
        e: Math.cos(-radRotation) * x - Math.sin(-radRotation) * y,
        n: Math.sin(-radRotation) * x + Math.cos(-radRotation) * y,
    };
}

/**
 * Convert from ENU to UVW. Ported from Matlab function enu2uvw.m
 * @param {number} e 
 * @param {number} n 
 * @param {number} u 
 * @param {number} buildingLat 
 * @param {number} buildingLng 
 */
function enuToUvw(e, n, u, buildingLat, buildingLng) {
    const latRad = degressToRadians(buildingLat);
    const lngRad = degressToRadians(buildingLng);
    const t = Math.cos(latRad) * u - Math.sin(latRad) * n;
    return {
        w: Math.sin(latRad) * u + Math.cos(latRad) * n,
        u: Math.cos(lngRad) * t - Math.sin(lngRad) * e,
        v: Math.sin(lngRad) * t + Math.cos(lngRad) * e,
    };
}

/**
 * Convert ENU (surface level) to ECEF (earth centric).
 * Ported from Matlab function enu2ecef.m
 * @param {Object} enuAndBuildingPrameters
 */
function enuToECEF({ enuE, enuN, enuU, buildingLat, buildingLng, buildingAlt }) {
    // first convert the building lat long to ECEF coordinates
    const [ecefX, ecefY, ecefZ] = projector.project(buildingLat, buildingLng, buildingAlt);
    const { w, u, v } = enuToUvw(enuE, enuN, enuU, buildingLat, buildingLng);

    const x = ecefX + u;
    const y = ecefY + v;
    const z = ecefZ + w;
    return { x, y, z };
}

/**
 * Convert from ENU coordinated system to Geodetic (lat, long, alt) one.
 * Ported from Matlab function enu2geodetic.m
 * @param {number} e 
 * @param {number} n 
 * @param {number} u 
 * @param {number} buildingLat 
 * @param {number} buildingLng 
 * @param {number} buildingAlt 
 */
function enuToGeodetic(e, n, u, buildingLat, buildingLng, buildingAlt) {
    // convert the local enu to ecef coordinates first
    const { x, y, z } = enuToECEF({ enuE: e, enuN: n, enuU: u, buildingLat, buildingLng, buildingAlt });

    // ecef to geodetic
    const [geodeticLat, geodeticLng, geodeticAlt] = projector.unproject(x, y, z);
    return {
        lat: geodeticLat,
        lng: geodeticLng,
        alt: geodeticAlt,
    };
}

/**
 * 
 * @param {number} buildingToEnuRotation rotation of building relative to north
 * @param {number} buildingLat building latitude 
 * @param {number} buildingLng building longitude
 * @param {number} buildingAlt building altitude
 * @param {x: number, y: number, height: number} coordinate coordintes to convert from building to latitude longitude 
 */
function buildingCoordinatesToLatLng(
    buildingToEnuRotation,
    buildingLat,
    buildingLng,
    buildingAlt,
    { x, y, height }
) {
    // convert the coordinates from building one to enu (rotate the coordinates to the north)
    const { e, n } = buildingToENU(buildingToEnuRotation, { x, y });
    // now convert the enu to lat long
    return enuToGeodetic(e, n, height, buildingLat, buildingLng, buildingAlt);
}

module.exports = {
    buildingCoordinatesToLatLng,
    enuToGeodetic,
    enuToECEF,
    buildingToENU,
    enuToUvw
};
